import { useMemo } from "react";
import { useRtkQueryDynamicEndpoint } from "../../../../../common/hooks/useRtkQueryDynamicEndpoint";
import { If } from "../../../../../common/components/If";
import ErrorMessageModal from "../../../../../common/components/ErrorMessageDialog";
import {
	ErrorType,
	formatApiErrorMessage,
} from "../../../../../common/utils/formatApiErrorMessage";
import { GeneratingCurtailmentNothingModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentNothingModal";
import { GeneratingCurtailmentObjectIncompleteModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentObjectIncompleteModal";
import { GeneratingCurtailmentSuccessModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentSuccessModal";
import { GeneratingCurtailmentLoadingModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentLoadingModal";
import { GeneratingCurtailmentObjectFailedModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentObjectFailedModal";
import { GenerateCurtailmentObjects } from "../../../../../common/BuildCurtailmentObjectsComponents/GenerateCurtailmentObjects";
import { useBuildCurtailmentObjectsFromContractPeriodMutation } from "../../../../../requests_cm/gecoContractsService/service";
import { SupportedCountry } from "../../../../../common/constants/countries";
import { mapCCOFormValuesToCurtailmentrequest } from "../../../../../common/BuildCurtailmentObjectsComponents/utils";

export enum BuildStatuses {
	initial = "initial",
	success = "success",
	loading = "loading",
	empty = "empty",
	failed = "failed",
	incomplete = "incomplete",
}

export interface BuildCurtailmentObjectsModalContainerProps {
	contractperiodId: number;
	country: string;
	isOpen: boolean;
	onClose: () => void;
}
export const BuildCurtailmentObjectsModalContainer = ({
	contractperiodId,
	isOpen,
	country,
	onClose,
}: BuildCurtailmentObjectsModalContainerProps) => {
	const [baseBuildCurtailmentObjects, { data, error, isLoading, reset }] =
		useBuildCurtailmentObjectsFromContractPeriodMutation();
	const buildCurtailmentObjects = useRtkQueryDynamicEndpoint(
		baseBuildCurtailmentObjects
	);
	const currentStatus = useMemo(() => {
		if (isLoading) {
			return BuildStatuses.loading;
		}
		if (data?.errors?.length && data.success.length) {
			return BuildStatuses.incomplete;
		}
		if (data && !data?.errors?.length && data.success.length) {
			return BuildStatuses.success;
		}
		if (data?.errors?.length && !data.success.length) {
			return BuildStatuses.failed;
		}
		if (data && !data?.errors?.length && !data.success.length) {
			return BuildStatuses.empty;
		}
		return BuildStatuses.initial;
	}, [data, isLoading]);

	const onCloseModal = () => {
		onClose();
		reset();
	};

	return (
		<>
			<If condition={isOpen}>
				<GenerateCurtailmentObjects
					isOpen={currentStatus === BuildStatuses.initial}
					onClose={onCloseModal}
					onSubmit={(values) =>
						buildCurtailmentObjects({
							contractperiod_id: contractperiodId,
							data: mapCCOFormValuesToCurtailmentrequest(
								values,
								country
							),
						})
					}
					country={country as SupportedCountry}
				/>
				<GeneratingCurtailmentLoadingModal
					isOpen={currentStatus === BuildStatuses.loading}
				/>
				<GeneratingCurtailmentNothingModal
					isOpen={currentStatus === BuildStatuses.empty}
					onClose={onCloseModal}
				/>
				<GeneratingCurtailmentSuccessModal
					isOpen={currentStatus === BuildStatuses.success}
					onClose={onCloseModal}
				/>
				<GeneratingCurtailmentObjectFailedModal
					errors={data?.errors || []}
					success={data?.success || []}
					isOpen={currentStatus === BuildStatuses.failed}
					onClose={onCloseModal}
				/>
				<GeneratingCurtailmentObjectIncompleteModal
					errors={data?.errors || []}
					success={data?.success || []}
					isOpen={currentStatus === BuildStatuses.incomplete}
					onClose={onCloseModal}
				/>
			</If>
			<ErrorMessageModal
				title={"Failed to generate curtailment object"}
				content={formatApiErrorMessage(error as ErrorType)}
				actionAfterClose={reset}
			/>
		</>
	);
};
